<template>
  <div class="team-incentive-details">
    <h2>{{ t('teamIncentiveDetails.Title') }}</h2>

    <div v-if="loadingReports" class="loading">{{ t('labels.Loading') }}...</div>
    <div v-else-if="error" class="error">{{ error }}</div>

    <div v-else>
      <div v-if="incentiveReports.length === 0" class="no-reports">
        {{ t('teamIncentiveDetails.NoReportsFound') }}
      </div>
      
      <div v-else class="report-selector">
        <div class="dropdown-container">
          <label for="incentive-dropdown">{{ t('teamIncentiveDetails.SelectIncentive') }}</label>
          <select 
            id="incentive-dropdown"
            v-model="selectedReportId" 
            @change="onReportSelected"
            class="incentive-dropdown"
          >
            <option value="">{{ t('teamIncentiveDetails.SelectPlaceholder') }}</option>
            <option 
              v-for="report in incentiveReports" 
              :key="report.reportId" 
              :value="report.reportId"
            >
              {{ report.localizedReportName }}
            </option>
          </select>
        </div>

        <div v-if="loadingReport" class="loading-report">
          <Loader />
        </div>

        <div v-else-if="selectedReportId && reportDefinition" class="report-content">
          <Grid
            :data-items="paginatedData"
            :columns="gridColumns"
            :resizable="true"
            :sortable="true"
            :pageable="{
              buttonCount: 5,
              pageSizes: [10, 20, 50, 100],
              info: true
            }"
            :skip="skip"
            :take="pageSize"
            :total="reportData.length"
            @pagechange="pageChange"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, h } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { Grid } from '@progress/kendo-vue-grid'
import type { GridPageChangeEvent } from '@progress/kendo-vue-grid'
import { Loader } from '@progress/kendo-vue-indicators'
import { 
  PwsDynamicReportGetList, 
  PwsDynamicReportGet, 
  PwsDynamicReportExecute,
  type DynamicReportDefinition 
} from '@/api/ConsultantCorner'
import { resolveColumnWidth } from '@/utils/kendoHelper.ts'

const { t } = useI18n()
const logger = useLogger()

const loadingReports = ref(true)
const loadingReport = ref(false)
const error = ref('')
const incentiveReports = ref<any[]>([])
const selectedReportId = ref('')
const reportDefinition = ref<DynamicReportDefinition | null>(null)
const reportData = ref<any[]>([])
const skip = ref(0)
const pageSize = ref(20)
const expandedRows = ref<Set<any>>(new Set())
const hasHierarchy = ref(false)

// Load incentive reports list
async function loadIncentiveReports() {
  loadingReports.value = true
  error.value = ''

  try {
    logger.debug('Loading incentive reports...')
    const response = await PwsDynamicReportGetList('Incentives')
    logger.debug('Incentive reports response:', response)

    if (response && response.category && response.category.length > 0) {
      // Extract reports from the Incentives category
      const incentiveCategory = response.category.find(
        (cat: any) => cat.codeIdDynamicReportCategory?.codeId === 'Incentives'
      )
      
      if (incentiveCategory && incentiveCategory.reports) {
        incentiveReports.value = incentiveCategory.reports
      } else {
        incentiveReports.value = []
      }
    } else {
      incentiveReports.value = []
    }
  } catch (err: any) {
    error.value = err.message || 'Failed to load incentive reports'
    logger.error('Error loading incentive reports:', err)
  } finally {
    loadingReports.value = false
  }
}

// Handle report selection
async function onReportSelected() {
  if (!selectedReportId.value) {
    reportDefinition.value = null
    reportData.value = []
    return
  }

  loadingReport.value = true
  error.value = ''

  try {
    // Step 1: Get report definition
    logger.debug('Getting report definition for:', selectedReportId.value)
    const definition = await PwsDynamicReportGet(selectedReportId.value)
    logger.debug('Report definition:', definition)
    reportDefinition.value = definition.item

    // Step 2: Execute report to get data
    await executeReport(selectedReportId.value, definition.item)
    
    // Check if this report has hierarchical data
    hasHierarchy.value = definition.item.columns.some(col => 
      col.columnName === 'ParentBeeNumber'
    )
  } catch (err: any) {
    error.value = err.message || 'Failed to load report'
    logger.error('Error loading report:', err)
  } finally {
    loadingReport.value = false
  }
}

// Execute report and get data
async function executeReport(reportId: string, definition: DynamicReportDefinition) {
  // Get column names (exclude filter-only columns)
  const columnsToFetch = definition.columns
    .filter((col: any) => !col.isFilterOnly)
    .map((col: any) => col.columnName)
  
  // Use filters from definition
  const filters = definition.filters || []
  
  logger.debug('Executing report with columns:', columnsToFetch)
  logger.debug('Executing report with filters:', filters)
  
  const result = await PwsDynamicReportExecute({
    reportId,
    ColumnNames: columnsToFetch,
    Filters: filters
  })

  logger.debug('Report data:', result)
  reportData.value = result.Items || []
}

// Build hierarchical data structure
const hierarchicalData = computed(() => {
  const data = reportData.value
  if (!hasHierarchy.value || !data.length) {
    return data.map(row => ({ ...row, _isChild: false, _hasChildren: false, _level: 0 }))
  }

  // Build a map of all children for each BeeNumber
  const childrenMap = new Map<any, any[]>()
  
  data.forEach(row => {
    const parentBeeNumber = row.ParentBeeNumber
    
    if (parentBeeNumber && parentBeeNumber !== '' && parentBeeNumber !== null) {
      if (!childrenMap.has(parentBeeNumber)) {
        childrenMap.set(parentBeeNumber, [])
      }
      childrenMap.get(parentBeeNumber)!.push(row)
    }
  })

  // Recursive function to build hierarchy with levels
  function buildHierarchy(parentBeeNumber: any, level: number): any[] {
    const result: any[] = []
    
    // Get all rows with this parent (or no parent if it's root level)
    const rows = parentBeeNumber === null 
      ? data.filter(row => !row.ParentBeeNumber || row.ParentBeeNumber === '' || row.ParentBeeNumber === null)
      : childrenMap.get(parentBeeNumber) || []
    
    rows.forEach(row => {
      const hasChildren = childrenMap.has(row.BeeNumber)
      const isChild = level > 0
      
      result.push({
        ...row,
        _isChild: isChild,
        _hasChildren: hasChildren,
        _level: level
      })
      
      // If this row is expanded and has children, add them recursively
      if (expandedRows.value.has(row.BeeNumber) && hasChildren) {
        result.push(...buildHierarchy(row.BeeNumber, level + 1))
      }
    })
    
    return result
  }

  return buildHierarchy(null, 0)
})

// Paginated data
const paginatedData = computed(() => {
  const data = hierarchicalData.value
  const start = skip.value
  const end = Math.min(start + pageSize.value, data.length)
  return data.slice(start, end)
})

// Grid columns
const gridColumns = computed(() => {
  if (!reportDefinition.value) return []

  const columns: any[] = []

  // Add expand/collapse column if hierarchy exists
  if (hasHierarchy.value) {
    columns.push({
      field: '_expand',
      title: '+',
      width: 50,
      cell: (h: any, tdElement: any, props: any) => {
        const row = props.dataItem
        const indentPx = (row._level || 0) * 20
        
        if (row._hasChildren) {
          // Row with children - show expand/collapse button
          const isExpanded = expandedRows.value.has(row.BeeNumber)
          return h('td', {
            class: 'expand-cell',
            style: { paddingLeft: `${indentPx}px` },
            onClick: () => toggleExpand(row.BeeNumber)
          }, [
            h('span', { 
              class: 'expand-icon',
              innerHTML: isExpanded ? '−' : '+'
            })
          ])
        }
        
        // Row without children - show indentation only
        return h('td', { 
          class: row._isChild ? 'child-row-cell' : '',
          style: { paddingLeft: `${indentPx + 20}px` }
        })
      }
    })
  }

  // Add regular columns
  const regularColumns = reportDefinition.value.columns
    .filter(col => !col.isFilterOnly)
    .filter(col => col.columnName !== 'ParentBeeNumber') // Hide ParentBeeNumber column
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((column, index) => ({
      field: column.columnName,
      title: column.localizedColumnName,
      width: resolveColumnWidth('200px', column.localizedColumnName),
      format: column.dataType === 'DateDataType' ? '{0:d}' : undefined,
      // Apply indentation to the first data column if hierarchy exists
      cell: hasHierarchy.value && index === 0 
        ? (h: any, tdElement: any, props: any) => {
            const row = props.dataItem
            const indentPx = (row._level || 0) * 20
            const content = props.dataItem[column.columnName]
            return h('td', {
              style: { paddingLeft: `${indentPx + 12}px` },
              class: row._isChild ? 'child-row-cell' : ''
            }, content)
          }
        : undefined
    }))

  return [...columns, ...regularColumns]
})

// Toggle expand/collapse for a parent row
function toggleExpand(beeNumber: any) {
  if (expandedRows.value.has(beeNumber)) {
    expandedRows.value.delete(beeNumber)
  } else {
    expandedRows.value.add(beeNumber)
  }
  // Force reactivity
  expandedRows.value = new Set(expandedRows.value)
}

// Page change handler
const pageChange = (event: GridPageChangeEvent) => {
  skip.value = event.page.skip
  pageSize.value = event.page.take
}

onMounted(() => {
  loadIncentiveReports()
})
</script>

<style lang="scss" scoped>
.team-incentive-details {
  padding: $card-padding;
}

.loading,
.error,
.no-reports {
  padding: $card-padding;
  text-align: center;
}

.error {
  color: red;
}

.report-selector {
  margin-top: 20px;
}

.dropdown-container {
  margin-bottom: 30px;
}

.dropdown-container label {
  @include typography-body;
  @include font-weight-semibold;
  display: block;
  margin-bottom: 10px;
  color: #333;
}

.incentive-dropdown {
  width: 100%;
  max-width: 500px;
  @include typography-small;
  padding: $spacing-2-5;
  border: 1px solid #cccccc;
  border-radius: $radius-sm;
  background-color: white;
  cursor: pointer;
}

.incentive-dropdown:focus {
  outline: none;
  border-color: var(--vt-c-teal-medium);
  box-shadow: 0 0 0 2px rgba(var(--vt-c-teal-medium), 0.1);
}

.loading-report {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.report-content {
  background: white;
  border-radius: $radius-sm;
  box-shadow: 0 2px 4px rgba(black, 0.1);
  padding: $card-padding;
}

/* Expand/collapse styling */
.expand-cell {
  cursor: pointer;
  text-align: center;
  user-select: none;
}

.expand-icon {
  display: inline-block;
  width: 20px;
  height: 20px;
  line-height: 18px;
  text-align: center;
  border: 1px solid #cccccc;
  border-radius: $radius-sm;
  @include typography-small;
  @include font-weight-bold;
  background-color: var(--vt-c-white-soft);
}

.expand-icon:hover {
  background-color: #e0e0e0;
  border-color: #999999;
}

.child-row-cell {
  background-color: var(--vt-c-white-soft);
}

:deep(.k-grid tr:has(.child-row-cell)) {
  background-color: var(--vt-c-white-soft);
}

:deep(.k-grid td) {
  vertical-align: top;
  padding: $spacing-2 $spacing-3;
}
</style>
